import { decamelizeKeys } from 'humps';
import { useState } from 'react';
import { showToast } from '@creditas-ui/react';
import type { SigninRequest, SigninResponseSuccess } from '@/shared/infrastructure/clients';
import {
  deleteSession,
  setSession,
  setUserSession,
} from '@/shared/infrastructure/adapters/storage';
import { trackingService } from '@/shared/application/services/TrackingService';
import { Actions, Categories, EventName, Labels, PartnerProduct } from '@/shared/domain/enums';
import { useNavigateForm } from '@/shared/application/hooks/useNavigateForm';
import { catchError } from '@/shared/application/services/catchErrorService';
import { signin } from '@/auth/infra/adapters/signinAdapter';
import { currentSession } from '@/auth/infra/adapters/currentSessionAdapter';
import { eventTrackService } from '@/auth/application/services/eventTrackService';

type UseLoginReturn = {
  loading: boolean;
  login: (loginRequest: SigninRequest) => void;
};

export const useLogin = (): UseLoginReturn => {
  const dispatch = trackingService();

  const { goToProposals, goToAutofinLogin } = useNavigateForm();

  const [loading, setLoading] = useState<boolean>(false);

  const blockCurrentSessionAutoFinOnly = async (products: string) => {
    if (products.includes(PartnerProduct.AutoFinancing) && products.split('|').length === 1) {
      deleteSession();
      goToAutofinLogin({ userType: 'autofin-only' });
    }
  };

  const login = async (loginRequest: SigninRequest) => {
    const categories = [Categories.Partnerships, Categories.Authentication, Categories.Login];
    setLoading(true);

    try {
      dispatch({
        event: EventName.Partners,
        category: categories,
        action: Actions.Submit,
      });
      const signinResponse = await signin(loginRequest);
      setSession(signinResponse.data as SigninResponseSuccess);
      const currentSessionResponse = await currentSession();

      await blockCurrentSessionAutoFinOnly(currentSessionResponse.products);

      const dbSession = {
        groupId: currentSessionResponse.source,
        affiliateNameAlias: currentSessionResponse.source,
        name: currentSessionResponse.name,
        fullName: currentSessionResponse.name,
        email: currentSessionResponse.email,
        loginDomain: currentSessionResponse.domain,
        userId: currentSessionResponse.userId,
        id: currentSessionResponse.userId,
        socialName: currentSessionResponse.socialName,
        products: currentSessionResponse.products,
        type: currentSessionResponse.type,
        cnpj: currentSessionResponse.cnpj,
        document: currentSessionResponse?.document,
        cellphone: currentSessionResponse?.phone,
      };

      await setUserSession(dbSession);

      dispatch({
        event: EventName.Partners,
        category: categories,
        action: [Actions.Submit, Labels.Success],
        ...dbSession,
      });

      await eventTrackService.identify(decamelizeKeys(dbSession));

      goToProposals();
    } catch (error: any) {
      catchError({
        error,
        onRequestError: (err) => {
          dispatch({
            event: EventName.Partners,
            category: categories,
            action: [Actions.Submit, Labels.Error],
            label: err?.response?.data,
          });
        },
      });
      showToast(`Ooops! Algo deu errado. Confira os dados e tente novamente!`, {
        emoji: {
          symbol: '😬',
          label: 'Smile face',
        },
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    login,
    loading,
  };
};
